import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Shield, Lock, Server, Eye, FileCheck, Users, Check } from "lucide-react";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/landing/ScrollReveal";
import { Logo } from "@/components/ui/Logo";
import { PublicFooter } from "@/components/layout/PublicFooter";
import { redirectToStripeCheckout } from "@/lib/stripe";
import { ThemeToggle } from "@/components/theme-toggle";

export default function SecurityPage() {
  const securityFeatures = [
    {
      icon: Lock,
      title: "Encryption at Rest & Transit",
      description: "All data is encrypted using AES-256 at rest and TLS 1.3 in transit. Your customer data never travels unprotected."
    },
    {
      icon: Server,
      title: "SOC 2 Type II Infrastructure",
      description: "Built on enterprise-grade cloud infrastructure with SOC 2 Type II compliance. Regular security audits and penetration testing."
    },
    {
      icon: Eye,
      title: "Access Controls",
      description: "Role-based access control (RBAC) with granular permissions. SSO/SAML available for Enterprise plans."
    },
    {
      icon: FileCheck,
      title: "GDPR Compliant",
      description: "Full GDPR compliance with data processing agreements. Your data belongs to you—export or delete anytime."
    },
    {
      icon: Users,
      title: "Team Security",
      description: "Two-factor authentication, session management, and activity logs. Know exactly who accessed what and when."
    },
    {
      icon: Shield,
      title: "99.9% Uptime SLA",
      description: "Enterprise-grade reliability with 99.9% uptime SLA. Multiple redundancy layers and automatic failover."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/40">
        <div className="container mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/">
              <Logo size="md" />
            </Link>
            <nav className="hidden md:flex items-center gap-6 text-sm">
              <Link to="/product" className="text-muted-foreground hover:text-foreground transition-colors">Product</Link>
              <Link to="/how-it-works" className="text-muted-foreground hover:text-foreground transition-colors">How It Works</Link>
              <Link to="/pricing" className="text-muted-foreground hover:text-foreground transition-colors">Pricing</Link>
              <Link to="/use-cases" className="text-muted-foreground hover:text-foreground transition-colors">Use Cases</Link>
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle className="h-8 w-8 shrink-0" />
            <Button variant="ghost" asChild>
              <Link to="/auth">Login</Link>
            </Button>
            <Button onClick={redirectToStripeCheckout}>
              Start Free Trial
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 px-4">
        <div className="container mx-auto max-w-5xl">
          <ScrollReveal>
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
              <Shield className="w-4 h-4" />
              Security & Trust
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight mb-6">
              Enterprise-Grade Security
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl">
              Your customer data is your most valuable asset. We treat it with the security it deserves.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Security Features */}
      <section className="py-20 px-4 bg-secondary/30">
        <div className="container mx-auto max-w-6xl">
          <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" staggerDelay={0.1}>
            {securityFeatures.map((feature, i) => (
              <StaggerItem key={i}>
                <div className="p-6 rounded-[var(--radius-card)] bg-card border h-full">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-4">
                    <feature.icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-semibold mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Data Handling */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-12 text-center">
              How We Handle Your Data
            </h2>
          </ScrollReveal>
          
          <StaggerContainer className="space-y-6" staggerDelay={0.1}>
            {[
              {
                title: "Data Ownership",
                content: "You own your data. Always. We're just custodians. Export all your data anytime in standard formats."
              },
              {
                title: "Data Retention",
                content: "Active account data is retained as long as your subscription is active. After cancellation, data is retained for 30 days before secure deletion."
              },
              {
                title: "Data Location",
                content: "Data is stored in secure data centers in EU and US. Enterprise customers can choose their data residency location."
              },
              {
                title: "Third-Party Sharing",
                content: "We never sell your data. Third-party integrations only access data you explicitly authorize. All vendors are vetted for security compliance."
              },
              {
                title: "Backup & Recovery",
                content: "Automated daily backups with point-in-time recovery. 99.9% uptime SLA with disaster recovery procedures."
              }
            ].map((item, i) => (
              <StaggerItem key={i}>
                <div className="p-6 rounded-xl border bg-card">
                  <h3 className="font-semibold mb-2">{item.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{item.content}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Compliance */}
      <section className="py-20 px-4 bg-secondary/30">
        <div className="container mx-auto max-w-4xl">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-12 text-center">
              Compliance & Certifications
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={0.1}>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { name: "GDPR", desc: "EU General Data Protection Regulation" },
                { name: "SOC 2", desc: "Service Organization Control" },
                { name: "ISO 27001", desc: "Information Security Management" }
              ].map((cert, i) => (
                <div key={i} className="p-6 rounded-xl bg-card border text-center">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-primary mx-auto mb-4">
                    <Shield className="w-8 h-8" />
                  </div>
                  <h3 className="font-semibold mb-1">{cert.name}</h3>
                  <p className="text-sm text-muted-foreground">{cert.desc}</p>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Enterprise Ready */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6">
                Enterprise Ready
              </h2>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                Built for teams that take security seriously. From startups to enterprises, we scale with your security requirements.
              </p>
              <ul className="space-y-4">
                {[
                  "SSO / SAML authentication",
                  "Custom data retention policies",
                  "Dedicated security reviews",
                  "Custom DPA agreements",
                  "Priority security support",
                  "Vendor security questionnaire available"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-foreground/80">{item}</span>
                  </li>
                ))}
              </ul>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <div className="p-8 rounded-2xl border bg-card shadow-lg">
                <h3 className="font-semibold mb-4">Need Security Documentation?</h3>
                <p className="text-muted-foreground text-sm mb-6">
                  We're happy to provide security documentation, complete vendor questionnaires, or schedule a security review call.
                </p>
                <Button variant="outline" className="w-full">
                  Contact Security Team
                </Button>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-secondary/30">
        <div className="container mx-auto max-w-3xl text-center">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-muted-foreground text-lg mb-8">
              Join teams who trust Decision Sales OS with their customer data.
            </p>
            <Button size="lg" onClick={redirectToStripeCheckout}>
              Start Free Trial
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </ScrollReveal>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
